import { useEffect, useState } from 'react';

interface LoadingScreenProps {
  minDuration?: number;
}

const LoadingScreen = ({ minDuration = 150 }: LoadingScreenProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    // Reveal content shortly after the app mounts. We deliberately do NOT wait for
    // window 'load' (which blocks on every image/font/tracker and pushes LCP past 4s
    // on mobile). The hero is already painted underneath, so this just fades the splash.
    const fadeTimer = setTimeout(() => setIsFading(true), minDuration);
    const hideTimer = setTimeout(() => setIsVisible(false), minDuration + 500);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  }, [minDuration]);

  if (!isVisible) return null;

  return (
    <div 
      className={`fixed inset-0 z-[9999] flex items-center justify-center transition-opacity duration-500 ${
        isFading ? 'opacity-0' : 'opacity-100'
      }`}
      style={{ background: 'linear-gradient(135deg, hsl(220 25% 8%) 0%, hsl(220 20% 14%) 100%)' }}
    >
      <div className="relative flex flex-col items-center">
        {/* Logo with pulse animation */}
        <div className="relative">
          <img 
            src="/logo-white.png" 
            alt="RLX" 
            className="h-12 w-auto animate-logo-pulse"
            fetchPriority="high"
          />
          {/* Subtle glow effect behind logo */}
          <div className="absolute inset-0 blur-xl opacity-30 animate-logo-glow" aria-hidden="true">
            <img 
              src="/logo-white.png" 
              alt=""
              role="presentation"
              className="h-12 w-auto"
            />
          </div>
        </div>
        
        {/* Minimal loading indicator */}
        <div className="mt-8 flex gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-white/40 animate-dot-pulse" style={{ animationDelay: '0ms' }} />
          <div className="w-1.5 h-1.5 rounded-full bg-white/40 animate-dot-pulse" style={{ animationDelay: '150ms' }} />
          <div className="w-1.5 h-1.5 rounded-full bg-white/40 animate-dot-pulse" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
};

export default LoadingScreen;
